<template>
  <ModuleWrapper :moduleId="moduleId" title="Filler Slide" @initializeData="initializeData">
    <template #main>
      <div class="tw-mb-2 tw-block tw-text-xs">
        Shown as a loading screen before prerecorded videos start and after they end when switching back to live
        speakers. If a filler slide is not uploaded, a black Goldcast logo will show instead. Image size 1920x1080
      </div>
      <div v-if="isFillerSlideDisabled" class="tw-mb-2 tw-block tw-text-xs">
        Filler slide cannot be changed less than one hour before the start of the event
      </div>
      <StudioSpinner v-if="isLoading" :coverContainer="false" size="xsmall" />
      <div v-else-if="!selectedFillerSlideAsset">
        <div class="tw-mb-4 tw-block tw-text-xs">Select a file with an aspect ratio of 1920x1080</div>
        <StudioButton
          trackingId="select-filler-slide-button"
          variant="outline"
          :disabled="isFillerSlideDisabled"
          :disabledReason="
            isFillerSlideDisabled
              ? 'Filler slide cannot be changed less than one hour before the start of the event'
              : ''
          "
          @click="showFillerSlideLibraryDialog = true"
        >
          <template #icon>
            <TablerPlus />
          </template>
          Select Filler Slide
        </StudioButton>
      </div>
      <FillerSlideLibraryListItem
        v-else
        :visibleActions="{
          openPreview: true,
          rename: true,
          remove: true
        }"
        alwaysShowActions
        :fillerSlideAsset="selectedFillerSlideAsset"
        :isCtaDisabled="isFillerSlideDisabled"
        @remove="handleFillerSlideRemoved"
        @rename-success="handleRenameSuccess($event)"
      />
      <FillerSlideLibraryDialog
        v-if="showFillerSlideLibraryDialog"
        title="Select Filler Slide"
        :maxSelections="1"
        @close="showFillerSlideLibraryDialog = false"
        @select-filler-slides="handleSelectFillerSlides"
      />
    </template>
  </ModuleWrapper>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';

import FillerSlideLibraryDialog from '@/studio/studioLibrary/fillerSlideLibrary/FillerSlideLibraryDialog.vue';
import FillerSlideLibraryListItem from '@/studio/studioLibrary/fillerSlideLibrary/FillerSlideLibraryListItem.vue';
import {
  FillerSlideAsset,
  getFillerSlideAsset
} from '@/studio/studioLibrary/fillerSlideLibrary/fillerSlideLibraryService';
import TablerPlus from '@/studioComponents/Icons/tabler/TablerPlus.vue';
import StudioButton from '@/studioComponents/StudioButton.vue';
import StudioSpinner from '@/studioComponents/StudioSpinner.vue';
import isEventAboutToStart from '@/utils/isEventAboutToStart';

export default defineComponent({
  name: 'FillerSlide',
  components: {
    ModuleWrapper,
    FillerSlideLibraryDialog,
    FillerSlideLibraryListItem,
    StudioSpinner,
    StudioButton,
    TablerPlus
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      selectedFillerSlideAsset: undefined as FillerSlideAsset | undefined,
      isLoading: false,
      showFillerSlideLibraryDialog: false
    };
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    ...mapGetters('branding', { getInitialModuleData: 'getInitialModuleData' }),
    ...mapGetters('studioEventList', {
      event: 'getCurrentEvent'
    }),
    isFillerSlideDisabled(): boolean {
      if (this.$route.params.templateId || this.event.is_blueprint) {
        return false;
      }

      return isEventAboutToStart({
        startTime: this.event.start_time,
        timeInMinutes: 60
      });
    }
  },
  methods: {
    ...mapMutations('branding', {
      setChangesMadeInEvent: 'setChangesMadeInEvent'
    }),
    ...mapActions('studioToast', { triggerToast: 'triggerToast' }),
    initializeData() {
      const moduleKeys = ['filler_slide'];
      const { filler_slide } = this.getInitialModuleData(moduleKeys);

      if (filler_slide) {
        this.isLoading = true;
        getFillerSlideAsset(filler_slide)
          .then(fillerSlideAsset => {
            this.selectedFillerSlideAsset = fillerSlideAsset;
          })
          .catch(() => {
            this.triggerToast({
              msg: 'The filler slide could not be loaded.',
              mode: 'error'
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.selectedFillerSlideAsset = undefined;
      }
    },
    handleSelectFillerSlides(fillerSlideAssets: FillerSlideAsset[]) {
      if (!fillerSlideAssets.length) {
        this.showFillerSlideLibraryDialog = false;
        return;
      }

      this.selectedFillerSlideAsset = fillerSlideAssets[0];
      this.showFillerSlideLibraryDialog = false;
      this.setChangesMadeInEvent({
        filler_slide: this.selectedFillerSlideAsset.id
      });
    },
    handleFillerSlideRemoved() {
      this.selectedFillerSlideAsset = undefined;
      this.setChangesMadeInEvent({ filler_slide: null });
    },
    handleRenameSuccess(fillerSlideAsset: FillerSlideAsset) {
      this.selectedFillerSlideAsset = fillerSlideAsset;
    }
  }
});
</script>
